import { CalendarDays } from 'lucide-react';
import { Popover as PopoverPrimitive } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { Calendar, parseLocalDay, toLocalDay } from './calendar.js';
import { controlBase } from './input.js';
import { Select, type SelectProps } from './select.js';

const dayLabel = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

/** "2026-09-23" → "23 sept 2026" (formatted as a calendar day; no timezone shift). */
export function formatLocalDay(day: string): string {
  return dayLabel.format(new Date(`${day}T12:00:00Z`));
}

export interface DatePickerProps {
  /** Local calendar day YYYY-MM-DD, or '' when empty. */
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  /** Inclusive bounds (YYYY-MM-DD). */
  min?: string;
  max?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

/** Single-day picker: a control-styled trigger that opens a Calendar in a popover. */
export const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(function DatePicker(
  { value, onValueChange, placeholder = 'Seleccionar fecha', min, max, disabled, id, className, ...aria },
  ref,
) {
  const [open, setOpen] = React.useState(false);
  const selected = value ? parseLocalDay(value) : undefined;
  const bounds = [
    ...(min ? [{ before: parseLocalDay(min) }] : []),
    ...(max ? [{ after: parseLocalDay(max) }] : []),
  ];
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger
        ref={ref}
        id={id}
        disabled={disabled}
        className={cn(controlBase, 'flex items-center gap-2 text-left', !value && 'text-text-muted', className)}
        {...aria}
      >
        <CalendarDays aria-hidden className="size-[18px] shrink-0 text-text-secondary" />
        <span className="cm-tabular truncate">{value ? formatLocalDay(value) : placeholder}</span>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          className="z-50 rounded-menu border border-border-subtle bg-surface-default shadow-overlay animate-fade-in"
        >
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected}
            disabled={bounds}
            onSelect={(d) => {
              if (!d) return;
              onValueChange(toLocalDay(d));
              setOpen(false);
            }}
          />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
});

function timeOptions(stepMinutes: number): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  for (let m = 0; m < 24 * 60; m += stepMinutes) {
    const v = `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    out.push({ value: v, label: v });
  }
  return out;
}

export interface TimeSelectProps extends Omit<SelectProps, 'options'> {
  /** Minutes between options (default 30). The current value is always offered even if off-step. */
  stepMinutes?: number;
}

/** Time-of-day select (HH:MM, 24h) built on the Radix Select. */
export const TimeSelect = React.forwardRef<HTMLButtonElement, TimeSelectProps>(function TimeSelect(
  { stepMinutes = 30, value, className, ...props },
  ref,
) {
  const options = React.useMemo(() => {
    const base = timeOptions(stepMinutes);
    if (value && !base.some((o) => o.value === value)) {
      base.push({ value, label: value });
      base.sort((a, b) => a.value.localeCompare(b.value));
    }
    return base;
  }, [stepMinutes, value]);
  return <Select ref={ref} value={value} options={options} className={cn('cm-tabular', className)} {...props} />;
});
